// WidgetRosterCard — Oct 2026 (Randy, Widget Roster Stage 4).
//
// "I want it to be compatible with a business model that allows a company,
// such as a psychology office, where they do counseling, or a chiropractic
// office… it could even be a salon that has multiple girls that work there."
//
// The host's side of the roster. One business embeds ONE widget and lists the
// people who work there; a visitor opens the hamburger, sees the staff with
// their faces, picks one, and the widget becomes that person's own assistant.
//
// Roster members are NOT members of this account. They are separate My Silo
// creators who accepted an invitation, and each one answers on THEIR OWN fuel
// under their own limits — so this panel shows the owner who is live and how
// busy they have been, and deliberately never shows what anyone's balance is.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  UsersThree, MagnifyingGlass, Trash, Clock, CheckCircle, WarningCircle, UserPlus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Face = ({ url, name, size = 40 }) => (
  <div
    className="rounded-full overflow-hidden bg-[#EFEBE0] border hairline shrink-0 flex items-center justify-center text-[#8A8780] font-medium"
    style={{ width: size, height: size, fontSize: size * 0.38 }}
  >
    {url
      ? <img src={url.startsWith("/") ? `${BACKEND_URL}${url}` : url} alt="" className="w-full h-full object-cover" />
      : (name || "?").trim().charAt(0).toUpperCase()}
  </div>
);

const RosterRow = ({ entry, onRemove, busy }) => {
  const pending = entry.status === "pending";
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-md border hairline bg-white"
      data-testid={`roster-row-${entry.id}`}>
      <Face url={entry.effective_avatar_url} name={entry.display_name} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#1C1B1A] truncate" data-testid={`roster-name-${entry.id}`}>
          {entry.display_name || entry.member_silo_name}
        </div>
        <div className="text-[11px] text-[#6B6A68] truncate flex items-center gap-1.5">
          {pending ? (
            <><Clock size={11} weight="duotone" /> Invited — waiting for them to accept</>
          ) : entry.available ? (
            <><CheckCircle size={11} weight="duotone" className="text-[#4A5D4E]" /> On your widget
              {entry.usage?.asks_total > 0 && (
                <span className="tabular-nums" data-testid={`roster-usage-${entry.id}`}>
                  · {entry.usage.asks_today} today, {entry.usage.asks_total} in all
                </span>
              )}
            </>
          ) : (
            <><WarningCircle size={11} weight="duotone" className="text-[#C16E5A]" />
              {entry.unavailable_reason || "Unavailable right now."}</>
          )}
        </div>
      </div>
      <button type="button" disabled={busy} onClick={() => onRemove(entry)}
        aria-label={`Remove ${entry.display_name || "this person"}`}
        data-testid={`roster-remove-${entry.id}`}
        className="shrink-0 p-1.5 rounded-md text-[#A8A6A1] hover:text-[#C16E5A] hover:bg-[#C16E5A]/[0.07] disabled:opacity-50">
        <Trash size={13} weight="duotone" />
      </button>
    </div>
  );
};

export default function WidgetRosterCard({ silo }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/silos/${silo.id}/widget/roster`);
      setData(r.data);
    } catch {
      // 402 (no SKU), 403 (not the owner) and anything else all mean the same
      // thing here: this person has no roster to manage, so say nothing.
      setHidden(true);
    }
  }, [silo.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setCandidates(null); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/silos/${silo.id}/widget/roster/search`, { params: { q: term } });
        setCandidates(r.data.candidates || []);
      } catch { setCandidates([]); }
    }, 320);
    return () => clearTimeout(timer.current);
  }, [q, silo.id]);

  const invite = async (c) => {
    setBusy(true);
    try {
      await api.post(`/silos/${silo.id}/widget/roster`, { member_silo_id: c.member_silo_id });
      toast.success(`Invited ${c.name}. They choose their own name and photo when they accept.`);
      setQ("");
      setCandidates(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not invite them.");
    } finally { setBusy(false); }
  };

  const remove = async (entry) => {
    const name = entry.display_name || entry.member_silo_name || "this person";
    if (!window.confirm(`Take ${name} off your widget?\n\nIt happens immediately — their assistant disappears from the menu on your site. You can invite them again later.`)) return;
    setBusy(true);
    try {
      await api.delete(`/silos/${silo.id}/widget/roster/${entry.id}`);
      toast.message(`${name} removed from your widget.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove them.");
    } finally { setBusy(false); }
  };

  const roster = useMemo(() => data?.roster || [], [data]);
  const live = roster.filter((e) => e.status === "accepted").length;
  const full = roster.length >= (data?.max_roster || 24);

  if (hidden || !data) return null;

  return (
    <section className="bg-[#FDFCF8] border hairline rounded-lg p-6 mb-6 siloart-keep"
      data-testid="widget-roster-card">
      <div className="flex items-center gap-2 mb-1">
        <UsersThree size={16} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Who's on your widget</h3>
      </div>
      <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl mb-4">
        Add the people who work with you and your website visitors can pick which one to ask — a
        practice, a clinic, a salon, one widget, everybody on it. Each person keeps their own
        silo and answers on their own fuel, so adding someone costs you nothing. They choose the
        name and photo your visitors see. Removing them takes effect immediately.
      </p>

      <div className="relative max-w-md">
        <MagnifyingGlass size={13} weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A6A1]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find them by email, silo name or address"
          disabled={busy || full}
          data-testid="roster-search-input"
          className="w-full text-sm pl-8 pr-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A] disabled:opacity-60"
        />
      </div>
      {full && (
        <p className="text-[11px] text-[#8A3A1C] mt-1.5" data-testid="roster-full">
          This widget already has {data.max_roster} people on it.
        </p>
      )}

      {candidates !== null && (
        <div className="mt-2 max-w-md space-y-1.5" data-testid="roster-candidates">
          {candidates.length === 0 && (
            <p className="text-[11px] text-[#6B6A68]" data-testid="roster-no-candidates">
              Nobody found. They need a My Silo account with a public silo, and they need to be a
              Creator.
            </p>
          )}
          {candidates.map((c) => (
            <div key={c.member_silo_id}
              className="flex items-center gap-3 py-2 px-3 rounded-md border hairline bg-white"
              data-testid={`roster-candidate-${c.slug || c.member_silo_id}`}>
              <Face url={c.silo_image_url} name={c.name} size={30} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#1C1B1A] truncate">{c.name}</div>
                <div className="text-[10.5px] text-[#6B6A68] truncate">
                  {c.already === "accepted" ? "Already on your widget"
                    : c.already === "pending" ? "Already invited"
                      : c.eligible ? (c.owner_name || `/s/${c.slug}`) : c.reason}
                </div>
              </div>
              {c.eligible && !c.already && (
                <button type="button" disabled={busy || full} onClick={() => invite(c)}
                  data-testid={`roster-invite-${c.slug || c.member_silo_id}`}
                  className="shrink-0 text-[11px] px-2.5 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
                  <UserPlus size={11} weight="duotone" /> Invite
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid="roster-list">
        {roster.length === 0 ? (
          <p className="text-[11px] text-[#6B6A68]" data-testid="roster-empty">
            Nobody yet. Your own assistant answers on its own, exactly as it does today.
          </p>
        ) : roster.map((e) => (
          <RosterRow key={e.id} entry={e} onRemove={remove} busy={busy} />
        ))}
      </div>

      {live > 0 && (
        <p className="text-[10.5px] text-[#6B6A68] mt-3" data-testid="roster-footnote">
          Your visitors see a menu in the widget with {live === 1 ? "this person" : `these ${live} people`} and
          your own assistant. Picking a different person always starts a fresh conversation.
        </p>
      )}
    </section>
  );
}
